import * as XLSX from "xlsx";
import { saveFileWithPicker } from "../../utils/fileManagerFunctions";
import { generateFilename } from "./download-utils/generateFilenameToExport";

// Run-2 column headers (single header row).
// Spelling matches observed template: "Identifed" is intentionally kept.
const RUN2_HEADERS = [
  "Vulnerability Name",
  "Description",
  "Instances",
  "Evidence",
  "Vulnerability Status",
  "Severity",
  "Remediation",
  "Vulnerability Identifed Date",
  "Vulnerability Remediation Status",
  "Vulnerability Remediation Date",
];

// Flatten instances to numbered multiline strings
function flattenInstancesAndEvidence(vuln) {
  // If Arachni/ZAP-like structure with instances array:
  if (Array.isArray(vuln.instances) && vuln.instances.length) {
    const instLines = [];
    const evdLines = [];
    vuln.instances.forEach((it, i) => {
      const n = i + 1;
      instLines.push(`${n}. ${it.url || ""}`.trim());
      evdLines.push(`${n}. ${it.evidence || "N/A"}`.trim());
    });
    return {
      instances: instLines.join("\n"),
      evidence: evdLines.join("\n"),
    };
  }
  // If flat strings exist:
  if (typeof vuln.Instances === "string" || typeof vuln.Evidence === "string") {
    return {
      instances: vuln.Instances || "",
      evidence: vuln.Evidence || "",
    };
  }
  return { instances: "", evidence: "" };
}

// Create the Run-2 tabular data (array-of-arrays) from serviceResponses
// Updated createRun2AoA function
function createRun2AoA(data) {
  // console.log("createRun2AoA - Input data:", data); // Debug log

  const aoa = [];
  aoa.push(RUN2_HEADERS);

  // Handle different data structures
  let vulnerabilities;
  if (Array.isArray(data)) {
    vulnerabilities = data;
  } else {
    console.error("createRun2AoA expected array but got:", typeof data);
    return aoa; // Return just headers if no valid data
  }

  vulnerabilities.forEach((v, index) => {
    // console.log(`Processing vulnerability ${index}:`, v); // Debug log

    const { instances, evidence } = flattenInstancesAndEvidence(v);

    // Map fields; leave others blank per requirement
    const row = [
      v["Vulnerability Name"] || "",
      v.Description || "",
      instances,
      evidence,
      v["Vulnerability Status"] || "",
      v.Severity || "",
      v.Remediation || v["Recommended Solutions"] || "",
      // Template header uses "Identifed"; accept either spelling in input
      v["Vulnerability Identified Date"] ||
        v["Vulnerability Identifed Date"] ||
        "",
      "", // Vulnerability Remediation Status (blank)
      "", // Vulnerability Remediation Date (blank)
    ];
    aoa.push(row);
  });

  // console.log("createRun2AoA - Final AoA:", aoa); // Debug log
  return aoa;
}

// Basic formatting similar to your Run-1 (headers bold, wrap text, widths)
function applyRun2Formatting(worksheet, aoa) {
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // Column widths tuned for Run-2 columns
  const colWidths = [
    { wch: 28 }, // Vulnerability Name
    { wch: 60 }, // Description
    { wch: 60 }, // Instances
    { wch: 40 }, // Evidence
    { wch: 16 }, // Vulnerability Status
    { wch: 12 }, // Severity
    { wch: 50 }, // Remediation
    { wch: 22 }, // Vulnerability Identifed Date
    { wch: 28 }, // Vulnerability Remediation Status
    { wch: 24 }, // Vulnerability Remediation Date
  ];
  worksheet["!cols"] = colWidths;

  // Header formatting and wrap text
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[addr];
      if (!cell) continue;

      cell.s = cell.s || {};
      // Wrap text and align top-left for readability
      cell.s.alignment = {
        wrapText: true,
        vertical: "top",
        horizontal: "left",
      };

      // Header row bold and light fill
      if (r === 0) {
        cell.s.font = { name: "Calibri", sz: 11, bold: true };
        cell.s.fill = { fgColor: { rgb: "E6E6FA" } };
        cell.s.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      }
    }
  }

  // Set row heights: header shorter, data rows taller
  worksheet["!rows"] = aoa.map((_, i) => ({
    hpt: i === 0 ? 25 : 40,
  }));
}

// Public API: mirrors Run-1 signature (serviceResponses, filename)
// Updated Run-2 export function with runType support
export const exportToDastXLSRun2 = (
  serviceResponses,
  filename,
  runType = "Run-2"
) => {
  // console.log("Run2 Export - Input data:", serviceResponses); // Debug log

  // 🔥 FIX: Handle the flattened data structure
  let vulnerabilityData;

  if (Array.isArray(serviceResponses)) {
    // Check if it's an array of vulnerability objects or nested arrays
    if (
      serviceResponses.length > 0 &&
      typeof serviceResponses[0] === "object" &&
      !Array.isArray(serviceResponses[0])
    ) {
      // It's already a flat array of vulnerability objects
      vulnerabilityData = serviceResponses;
    } else if (Array.isArray(serviceResponses[0])) {
      // It's a nested array, flatten it
      vulnerabilityData = serviceResponses.flat();
    } else {
      vulnerabilityData = serviceResponses;
    }
  } else {
    vulnerabilityData = [serviceResponses];
  }

  // console.log("Run2 Export - Processed data:", vulnerabilityData); // Debug log

  // Validate we have data
  if (!vulnerabilityData || vulnerabilityData.length === 0) {
    console.error("No vulnerability data to export");
    alert("No data available to export");
    return;
  }

  const workbook = XLSX.utils.book_new();

  // Build Run-2 data shape using the flattened vulnerability data
  const aoa = createRun2AoA(vulnerabilityData);

  // console.log("Run2 Export - AoA data:", aoa); // Debug log

  // 🎯 UPDATED: Clean sheet name
  const sheetTitle = `DAST Run2`;
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // Apply formatting
  applyRun2Formatting(worksheet, aoa);

  // Append to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);

  // Write workbook and save
  const wbout = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });

  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // 🎯 UPDATED: Include runType in filename
  const dateStr = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
  const runTypeClean = runType.replace("-", ""); // Convert "Run-2" to "Run2"
  const extendedFileName = `DAST_Report_${runTypeClean}_${dateStr}.xlsx`;

  saveFileWithPicker(blob, extendedFileName);
};
